// Özet "Onay bekleyen (müşteri girdileri)" kuyruğu (M16 T14/T19, DR7, mockup 17).
// Satırlar: gösterge · yıl · işletme | önceki onaylı (yıl-1) | yeni | kim · ne zaman | kanıt | Onayla.
// Toplu onay C4: `verifyKpiValues` kısmi sonuç → modal "N onaylandı · M değişti — yeniden bakın".
// "Açıklama iste" (DR6): tek satır not → clarification. Formül varyantı talepleri ayrı blok
// (T19: `GET /kpi/sets/:setId/pending-variants`; Onayla/Reddet → `variant-verify`, C10).
// Boş durum: KPI_COPY.queue.empty ("✓ Danışman onayında değer yok") + " · son onay <tarih>" (kişi adı DTO'da yok — yalnız tarih).
// güvenlik/UX taraması 2026-10-08 (U-M3): "Seçilileri onayla" önce ConfirmDialog ("N girdi
// onaylanacak"); diyaloglar KpiApprovalQueueDialogs.tsx'te (600 satır sınırı).
// Dalga 3: müşteri notu hücre DTO'sundan çıktı — satırda SON müşteri notu not iş parçacığından okunur
// (`KpiQueueNoteLine`) + "Notları aç (n)" → iş parçacığı modalı (danışman yanıtlayabilir).
// TODO-KPI-10: `client_approved` satırlar da kuyruktadır ("Şirket onaylı" rozeti + "Yalnız şirket
// onaylı" filtresi) — danışman onayı yine gerekir. TODO-KPI-14: varyant satırında hesap etkisi
// ("2025: 4,46 → 4,52", sunucu `impact`).

import { useEffect, useMemo, useState } from "react";
import { formatKpiNumber, type KpiNoteSummaryCell, type KpiResolvedGrid } from "@mahir/shared";
import {
  listKpiPendingVariants,
  requestKpiClarification,
  verifyKpiValues,
  verifyKpiVariant,
  type KpiPendingVariant,
  type KpiValueDto,
} from "../../api/kpi";
import { collectQueue, type QueueItem } from "../../lib/kpi-queue";
import { noteCellKey } from "../../lib/kpi-notes-ui";
import { formatKpiCell, KPI_COPY } from "../../lib/kpi-ui";
import { lastVerifiedAt } from "../../lib/portal-kpi-ui";
import { KpiQueueBulkDialogs, KpiQueueClarifyDialog } from "./KpiApprovalQueueDialogs";
import { KpiNoteThreadModal } from "./KpiNoteThreadModal";
import { KpiQueueNoteLine } from "./KpiQueueNoteLine";
import { KpiQueueOutsideYears } from "./KpiQueueOutsideYears";

const td: React.CSSProperties = {
  padding: "8px 10px",
  borderBottom: "1px solid var(--color-border)",
  fontSize: "0.85rem",
  color: "var(--color-text)",
  whiteSpace: "nowrap",
};
const th: React.CSSProperties = {
  ...td,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-3)",
  textAlign: "left",
};

export function KpiApprovalQueue({
  grid,
  onChanged,
  canVerify,
  singleEntity,
  years,
  onExtendYears,
  notes,
}: {
  grid: KpiResolvedGrid;
  /** Dalga 3 — hücre not özeti (Özet sayfasının tek `note-summary` çağrısı) + yeniden çek */
  notes?: { summary: Map<string, KpiNoteSummaryCell>; reload: () => void } | undefined;
  onChanged: () => void;
  canVerify: boolean;
  singleEntity: boolean;
  /** yüklü yıl aralığı — kuyruk grid'den türediği için aralık dışı onay bekleyenler görünmez */
  years?: number[] | undefined;
  /** "Aralığı genişlet": aralık ∪ onay bekleyen yıllar */
  onExtendYears?: ((years: number[]) => void) | undefined;
}): JSX.Element {
  const allItems = useMemo(() => collectQueue(grid), [grid]);
  // 2026-10-08: aralık dışı yıla yazılmış onay bekleyenler → KpiQueueOutsideYears uyarısı
  const outsideBanner = (
    <KpiQueueOutsideYears
      setId={grid.setId}
      years={years}
      onExtendYears={onExtendYears}
      refreshKey={grid}
    />
  );
  // TODO-KPI-10 — "Yalnız şirket onaylı" filtresi (yalnız böyle satır varsa görünür)
  const [onlyClientApproved, setOnlyClientApproved] = useState(false);
  const clientApprovedCount = useMemo(
    () => allItems.filter((i) => i.cell.workflowState === "client_approved").length,
    [allItems],
  );
  const items = useMemo(
    () =>
      onlyClientApproved && clientApprovedCount > 0
        ? allItems.filter((i) => i.cell.workflowState === "client_approved")
        : allItems,
    [allItems, onlyClientApproved, clientApprovedCount],
  );
  // T19/DR7 — varyant talepleri ayrı uçtan (talep eden kişi adı + zaman)
  const [variants, setVariants] = useState<KpiPendingVariant[]>(() =>
    grid.indicators
      .filter((i) => i.pendingFormulaId)
      .map((i) => ({
        indicatorId: i.id,
        setId: i.setId,
        metricId: i.metricId,
        code: i.code,
        label: i.label,
        activeFormula: i.formulas.find((f) => f.id === i.activeFormulaId) ?? null,
        pendingFormula: i.formulas.find((f) => f.id === i.pendingFormulaId) ?? null,
        requestedBy: null,
        requestedAt: i.pendingRequestedAt,
        impact: [],
      })),
  );
  const [variantBusy, setVariantBusy] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    listKpiPendingVariants(grid.setId)
      .then((v) => alive && setVariants(v))
      .catch(() => {
        /* grid türevi yedek liste kalır */
      });
    return () => {
      alive = false;
    };
  }, [grid]);
  const lastApproval = useMemo(() => lastVerifiedAt(grid), [grid]);

  async function decideVariant(indicatorId: string, approve: boolean): Promise<void> {
    setVariantBusy(indicatorId);
    setError(null);
    try {
      await verifyKpiVariant(indicatorId, approve);
      setVariants((prev) => prev.filter((v) => v.indicatorId !== indicatorId));
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Varyant kararı kaydedilemedi");
    } finally {
      setVariantBusy(null);
    }
  }
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(items.map((i) => i.cell.valueId!).filter(Boolean)),
  );
  // P2-13: tek "Onayla" sonrası "N seçili" sayacı eski kalıyordu — kuyruktan düşen satırlar seçimden
  // de düşer (grid yenilenince items değişir); yeni gelen satır seçime eklenmez (kullanıcı seçer).
  useEffect(() => {
    const live = new Set(items.map((i) => i.cell.valueId!).filter(Boolean));
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ verified: number; conflicts: KpiValueDto[] } | null>(null);
  /** U-M3 — toplu onay onayı bekleyen valueId listesi (null = kapalı) */
  const [bulkConfirm, setBulkConfirm] = useState<string[] | null>(null);
  const [clarify, setClarify] = useState<QueueItem | null>(null);
  const [threadFor, setThreadFor] = useState<QueueItem | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  // P2-13: "Açıklama iste" diyaloğu Esc ile kapanır (odak nerede olursa olsun)
  useEffect(() => {
    if (!clarify) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setClarify(null);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [clarify]);

  async function approve(ids: string[]): Promise<void> {
    const payload = items
      .filter((i) => i.cell.valueId && ids.includes(i.cell.valueId))
      .map((i) => ({ valueId: i.cell.valueId!, expectedVersion: i.cell.version ?? 0 }));
    if (payload.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const r = await verifyKpiValues(payload);
      setResult({ verified: r.verified.length, conflicts: r.conflicts });
      const done = new Set(r.verified);
      setSelected((prev) => new Set([...prev].filter((id) => !done.has(id))));
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Onaylanamadı");
    } finally {
      setBusy(false);
    }
  }

  async function sendClarification(): Promise<void> {
    if (!clarify?.cell.valueId) return;
    setBusy(true);
    setError(null);
    try {
      await requestKpiClarification(clarify.cell.valueId, clarify.cell.version ?? 0, note.trim());
      setClarify(null);
      setNote("");
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gönderilemedi");
    } finally {
      setBusy(false);
    }
  }

  if (allItems.length === 0 && variants.length === 0) {
    return (
      <div>
        {outsideBanner}
        <div
          className="rounded-xl p-6 text-sm"
          style={{
            border: "1px solid var(--color-border)",
            color: "var(--color-success)",
            background: "var(--color-surface)",
          }}
        >
          {KPI_COPY.queue.empty}
          {lastApproval
            ? ` · son onay ${new Date(lastApproval).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" })}`
            : ""}
        </div>
      </div>
    );
  }

  return (
    <div>
      {outsideBanner}
      <div
        className="rounded-xl"
        style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
      >
        <div
          className="flex items-center gap-2 px-3 py-2 flex-wrap"
          style={{ borderBottom: "1px solid var(--color-border)" }}
        >
          <span className="text-xs" style={{ color: "var(--color-text-2)" }}>
            {selected.size} seçili
          </span>
          {canVerify ? (
            <button
              type="button"
              disabled={busy || selected.size === 0}
              // güvenlik/UX taraması 2026-10-08 (U-M3): toplu onay önce "N girdi onaylanacak" onayı
              onClick={() => setBulkConfirm([...selected])}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold"
              style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            >
              Seçilileri onayla
            </button>
          ) : null}
          {clientApprovedCount > 0 ? (
            <label
              className="text-xs flex items-center gap-1"
              style={{ color: "var(--color-text-2)" }}
              data-testid="kpi-queue-client-approved-filter"
            >
              <input
                type="checkbox"
                checked={onlyClientApproved}
                onChange={(e) => setOnlyClientApproved(e.target.checked)}
              />
              {KPI_COPY.queue.clientApprovedFilter} ({clientApprovedCount})
            </label>
          ) : null}
          <span style={{ flex: 1 }} />
          <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
            Onaylanan değer rapora ve AI'ya sayı olarak gider; onaysız kalan XXX
          </span>
        </div>
        {error ? (
          <div role="alert" className="text-xs px-3 py-2" style={{ color: "var(--color-error)" }}>
            {error}
          </div>
        ) : null}
        {/* P2-3: görünür yatay kaydırma + sağa yapışık aksiyon kolonu (Onayla / Açıklama iste 1440'ta
          ekran dışında kalıyordu) — globals.css `.kpi-scroll-x` / `.kpi-sticky-right` */}
        <div className="kpi-scroll-x">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...th, width: 30 }} />
                <th style={th}>Gösterge · yıl{singleEntity ? "" : " · işletme"}</th>
                <th style={{ ...th, textAlign: "right" }}>Önceki onaylı</th>
                <th style={{ ...th, textAlign: "right" }}>Yeni</th>
                <th style={th}>Kim · ne zaman</th>
                <th style={th}>Kanıt</th>
                <th className={canVerify ? "kpi-sticky-right" : undefined} style={th} />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const id = it.cell.valueId!;
                const isPct = it.indicator.valueType === "percent";
                return (
                  <tr key={id}>
                    <td style={td}>
                      <input
                        type="checkbox"
                        aria-label="seç"
                        checked={selected.has(id)}
                        onChange={(e) =>
                          setSelected((prev) => {
                            const n = new Set(prev);
                            if (e.target.checked) n.add(id);
                            else n.delete(id);
                            return n;
                          })
                        }
                      />
                    </td>
                    <td style={td}>
                      {it.indicator.label} · {it.year}
                      {singleEntity ? "" : ` · ${it.entityName}`}
                      {it.cell.workflowState === "clarification" ? (
                        <span
                          className="ml-2"
                          style={{
                            fontSize: "0.66rem",
                            fontWeight: 700,
                            color: "var(--color-error)",
                          }}
                        >
                          AÇIKLAMA İSTENDİ
                        </span>
                      ) : null}
                      {it.cell.clarificationNote ? (
                        <div
                          className="text-xs"
                          data-testid="kpi-queue-clarification-note"
                          style={{ color: "var(--color-error)" }}
                        >
                          {KPI_COPY.queue.clarificationAsked}: {it.cell.clarificationNote}
                        </div>
                      ) : null}
                      {notes ? (
                        <KpiQueueNoteLine
                          setId={grid.setId}
                          cell={{
                            metricId: it.indicator.metricId,
                            entityId: it.entityId,
                            year: it.year,
                          }}
                          summary={notes.summary.get(
                            noteCellKey({
                              metricId: it.indicator.metricId,
                              entityId: it.entityId,
                              year: it.year,
                            }),
                          )}
                          onOpen={() => setThreadFor(it)}
                        />
                      ) : null}
                      {it.cell.workflowState === "client_approved" ? (
                        <span
                          className="ml-2"
                          data-testid="kpi-queue-client-approved"
                          style={{
                            display: "inline-block",
                            padding: "1px 8px",
                            borderRadius: 999,
                            fontSize: "0.66rem",
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                            background:
                              "var(--color-primary-soft, color-mix(in srgb, var(--color-primary) 10%, white))",
                            color: "var(--color-primary)",
                          }}
                        >
                          {KPI_COPY.queue.clientApprovedBadge}
                        </span>
                      ) : null}
                    </td>
                    <td
                      style={{
                        ...td,
                        textAlign: "right",
                        color: "var(--color-text-2)",
                        textDecoration: "line-through",
                      }}
                    >
                      {formatKpiCell(it.prev, { percent: isPct }).text}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>
                      {formatKpiCell(it.cell, { percent: isPct }).text}
                    </td>
                    <td style={{ ...td, fontSize: "0.75rem", color: "var(--color-text-2)" }}>
                      {it.cell.updatedByKind === "client" ? "Müşteri" : "Danışman"} ·{" "}
                      {it.cell.updatedAt
                        ? new Date(it.cell.updatedAt).toLocaleString("tr-TR", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </td>
                    <td style={{ ...td, fontSize: "0.75rem", color: "var(--color-text-3)" }}>
                      {it.cell.evidenceCount ? `📎 ${it.cell.evidenceCount}` : "—"}
                    </td>
                    <td
                      className={canVerify ? "kpi-sticky-right" : undefined}
                      style={{ ...td, textAlign: "right" }}
                    >
                      {canVerify ? (
                        <>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void approve([id])}
                            className="px-2 py-1 rounded-lg text-xs"
                            style={{
                              border: "1px solid var(--color-border)",
                              color: "var(--color-primary)",
                              minHeight: 28,
                            }}
                          >
                            Onayla
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              setClarify(it);
                              setNote(it.cell.clarificationNote ?? "");
                            }}
                            className="px-2 py-1 rounded-lg text-xs ml-1"
                            style={{
                              border: "1px solid var(--color-border)",
                              color: "var(--color-text-2)",
                              minHeight: 28,
                            }}
                          >
                            Açıklama iste
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
              {variants.length > 0 ? (
                <>
                  <tr>
                    <td
                      colSpan={7}
                      data-testid="kpi-variant-requests"
                      style={{
                        ...td,
                        background: "var(--color-bg)",
                        fontWeight: 700,
                        color: "var(--color-primary)",
                        fontSize: "0.78rem",
                      }}
                    >
                      Formül varyantı talepleri ({variants.length})
                    </td>
                  </tr>
                  {variants.map((v) => {
                    const isPct =
                      grid.indicators.find((i) => i.id === v.indicatorId)?.valueType === "percent";
                    const fmt = (n: number | null): string =>
                      n === null ? "—" : formatKpiNumber(n, { percent: isPct });
                    return (
                      <tr key={v.indicatorId}>
                        <td style={td} />
                        <td style={td}>
                          {v.label}
                          {v.impact.length > 0 ? (
                            <div
                              data-testid="kpi-variant-impact"
                              className="text-xs"
                              style={{ color: "var(--color-text-2)", marginTop: 2 }}
                            >
                              {v.impact.map((row) => (
                                <div key={row.year}>
                                  {row.year}: {fmt(row.current)} → <b>{fmt(row.proposed)}</b>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </td>
                        <td
                          style={{
                            ...td,
                            textAlign: "right",
                            color: "var(--color-text-2)",
                            textDecoration: "line-through",
                          }}
                        >
                          {v.activeFormula?.label ?? "—"}
                        </td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>
                          {v.pendingFormula?.label ?? "?"}
                        </td>
                        <td style={{ ...td, fontSize: "0.75rem", color: "var(--color-text-2)" }}>
                          {v.requestedBy?.fullName ?? "Müşteri"}
                          {v.requestedAt
                            ? ` · ${new Date(v.requestedAt).toLocaleString("tr-TR", {
                                day: "2-digit",
                                month: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}`
                            : ""}
                        </td>
                        <td style={td}>—</td>
                        <td
                          className={canVerify ? "kpi-sticky-right" : undefined}
                          style={{ ...td, textAlign: "right" }}
                        >
                          {canVerify ? (
                            <>
                              <button
                                type="button"
                                disabled={variantBusy === v.indicatorId}
                                onClick={() => void decideVariant(v.indicatorId, true)}
                                className="px-2 py-1 rounded-lg text-xs"
                                style={{
                                  border: "1px solid var(--color-border)",
                                  color: "var(--color-primary)",
                                  minHeight: 28,
                                }}
                              >
                                Onayla
                              </button>
                              <button
                                type="button"
                                disabled={variantBusy === v.indicatorId}
                                onClick={() => void decideVariant(v.indicatorId, false)}
                                className="px-2 py-1 rounded-lg text-xs ml-1"
                                style={{
                                  border: "1px solid var(--color-border)",
                                  color: "var(--color-error)",
                                  minHeight: 28,
                                }}
                              >
                                Reddet
                              </button>
                            </>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </>
              ) : null}
            </tbody>
          </table>
        </div>
        {threadFor && notes ? (
          <KpiNoteThreadModal
            open
            title={KPI_COPY.notes.threadFor(threadFor.indicator.label, threadFor.year)}
            setId={grid.setId}
            cell={{
              metricId: threadFor.indicator.metricId,
              entityId: threadFor.entityId,
              year: threadFor.year,
            }}
            onClose={() => setThreadFor(null)}
            onChanged={notes.reload}
          />
        ) : null}
        {clarify ? (
          <KpiQueueClarifyDialog
            item={clarify}
            note={note}
            busy={busy}
            onNote={setNote}
            onCancel={() => setClarify(null)}
            onSend={() => void sendClarification()}
          />
        ) : null}
        <KpiQueueBulkDialogs
          confirmCount={bulkConfirm ? bulkConfirm.length : null}
          busy={busy}
          onConfirm={() => {
            const ids = bulkConfirm ?? [];
            setBulkConfirm(null);
            void approve(ids);
          }}
          onCancelConfirm={() => setBulkConfirm(null)}
          result={result}
          onCloseResult={() => setResult(null)}
        />
      </div>
    </div>
  );
}
